// FIXME: Move rest of parsing logic to xstate (insurance).
// TODO: Move state persistence to url to xstate?
import equal from 'fast-deep-equal'
import { useAtom, useAtomValue } from 'jotai'
import * as qs from 'qs'
import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { SupportedLanguage } from '../../../__generated__/api'
import {
  APPOINTMENT_PARAMETERS,
  GLOBAL_PARAMETERS,
  MARKETING_PARAMETERS,
  SEARCH_PARAMETERS,
} from '../../../common/constants/urlParameters'
import useChangeLanguage from '../../../common/hooks/useChangeLanguage'
import { externalUrlParameters } from '../../../common/utils/url'
import { selectedInsuranceAtom, selectedInsurancePayerAtom } from '../../../state/common/atoms'
import { preSelectedUserOmIdAtom } from '../../../state/reserve/atoms'
import {
  defaultAppointmentSearchMode,
  defaultSelectedAppointmentListVariant,
  defaultSelectedAppointmentTypes,
  defaultSelectedDate,
  defaultSelectedGender,
  defaultSelectedLanguage,
  defaultSelectedLocation,
  defaultSelectedPatientAgeGroup,
  defaultSelectedStandardScheduleOption,
  selectedAppointmentListVariantAtom,
  defaultSelectedTimeRange,
} from '../../../state/search/atoms'
import { useSearchEvents, useURLEvents } from '../../../xstate/events'
import {
  useAppointmentSearchMode,
  useDefaultNode,
  useIsUserSelectedDate,
  useIsUserSelectedNode,
  useSearchFilterOptions,
  useSearchLocation,
  useSearchTarget,
  useSelectedDate,
  useSelectedStandardScheduleOption,
} from '../../../xstate/selectors'
import { SearchTargetValue } from '../../../xstate/types'

export const locationParamKey = 'lo'

const useSearchHistory = (): void => {
  const history = useHistory()

  const searchTarget = useSearchTarget()
  const searchFilters = useSearchFilterOptions()
  const selectedDate = useSelectedDate()
  const selectedLocation = useSearchLocation()
  const selectedAppointmentListVariant = useAtomValue(selectedAppointmentListVariantAtom)
  const [selectedInsurance, setSelectedInsurance] = useAtom(selectedInsuranceAtom)
  const [selectedInsurancePayer, setSelectedInsurancePayer] = useAtom(selectedInsurancePayerAtom)
  const [preSelectedUserOmId, setPreSelectedUserOmId] = useAtom(preSelectedUserOmIdAtom)
  const appointmentSearchMode = useAppointmentSearchMode()
  const selectedStandardScheduleOption = useSelectedStandardScheduleOption()
  const isUserSelectedDate = useIsUserSelectedDate()
  const isUserSelectedNode = useIsUserSelectedNode()
  const defaultNode = useDefaultNode()
  const { currentLanguage } = useChangeLanguage()
  const { setSearchParams } = useURLEvents()
  const { setSearchLocation } = useSearchEvents()
  const whiteListedSearch = useRef<qs.ParsedQs | null>(null)

  useEffect(() => {
    const searchString = history.location.search.slice(1)
    if (searchString !== '') {
      const parsed = qs.parse(searchString, { arrayLimit: 1000 })
      whiteListedSearch.current = getWhitelistedSearch(parsed)
      parsed.in && setSelectedInsurance(Number(parsed.in))
      parsed.inp && setSelectedInsurancePayer(parsed.inp as string)
      parsed.omUid && setPreSelectedUserOmId(Number(parsed.omUid))
      if (parsed.sourceService) {
        sessionStorage.setItem('av3_sourceService', String(parsed.sourceService))
      }
      if (parsed[locationParamKey]) {
        const loc = parsed[locationParamKey]
        setSearchLocation(Array.isArray(loc) ? (loc as string[]) : [loc as string])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Serialize relevant state to url query string
  useEffect(() => {
    const search = new URLSearchParams(
      qs.stringify({
        ...Object.assign(
          {},
          whiteListedSearch.current ?? {},
          ...[
            [selectedLocation, defaultSelectedLocation, locationParamKey],
            [searchFilters.gender, defaultSelectedGender, SEARCH_PARAMETERS.GENDER],
            [searchFilters.timeRange, defaultSelectedTimeRange, SEARCH_PARAMETERS.TIME_RANGE],
            [
              searchFilters.appointmentTypes,
              defaultSelectedAppointmentTypes,
              SEARCH_PARAMETERS.APPOINTMENT_TYPE,
            ],
            [selectedAppointmentListVariant, defaultSelectedAppointmentListVariant, 'lv'],
            [
              searchFilters.patientAgeGroup,
              defaultSelectedPatientAgeGroup,
              SEARCH_PARAMETERS.PATIENT_AGE_GROUP,
            ],
            searchTarget.value === SearchTargetValue.Practitioner
              ? [searchTarget.id, undefined, SEARCH_PARAMETERS.PRACTITIONER_ID]
              : [searchTarget.id, defaultNode, SEARCH_PARAMETERS.NODE_ID, isUserSelectedNode],
            [currentLanguage, SupportedLanguage.Fi, GLOBAL_PARAMETERS.LANG],
            [searchFilters.language, defaultSelectedLanguage, SEARCH_PARAMETERS.LANGUAGE],
            [appointmentSearchMode, defaultAppointmentSearchMode, APPOINTMENT_PARAMETERS.MODE],
            [
              selectedStandardScheduleOption,
              defaultSelectedStandardScheduleOption,
              SEARCH_PARAMETERS.REASON,
            ],
          ].map(([value, defaultValue, key, specialRule]) => ({
            ...(!equal(value, defaultValue) || specialRule ? { [key as string]: value } : {}),
          }))
        ),
        ...(!selectedDate.isSame(defaultSelectedDate, 'day') && isUserSelectedDate
          ? { [SEARCH_PARAMETERS.DATE]: selectedDate.toISOString() }
          : {}),
        ...(selectedInsurance ? { [SEARCH_PARAMETERS.INSURANCE]: selectedInsurance } : {}),
        ...(selectedInsurancePayer
          ? { [SEARCH_PARAMETERS.INSURANCE_PAYER]: selectedInsurancePayer }
          : {}),
        ...(preSelectedUserOmId ? { [GLOBAL_PARAMETERS.OM_UID]: preSelectedUserOmId } : {}),
      })
    )
    setSearchParams(search)
  }, [
    history,
    searchTarget,
    searchFilters,
    selectedDate,
    selectedLocation,
    selectedAppointmentListVariant,
    selectedInsurance,
    selectedInsurancePayer,
    appointmentSearchMode,
    selectedStandardScheduleOption,
    isUserSelectedDate,
    isUserSelectedNode,
    currentLanguage,
    defaultNode,
    setSearchParams,
    preSelectedUserOmId,
  ])
}

// Retain these GET parameters for marketing
const whitelist: string[] = [
  MARKETING_PARAMETERS.GCLID,
  MARKETING_PARAMETERS.DCLID,
  MARKETING_PARAMETERS.UTM_SOURCE,
  MARKETING_PARAMETERS.UTM_MEDIUM,
  MARKETING_PARAMETERS.UTM_CAMPAIGN,
  MARKETING_PARAMETERS.UTM_CONTENT,
  MARKETING_PARAMETERS.UTM_TERM,
  MARKETING_PARAMETERS.UTM_ID,
  GLOBAL_PARAMETERS.OM_UID,
]

const getWhitelistedSearch = (parsed: qs.ParsedQs): qs.ParsedQs => {
  const emptyObj: qs.ParsedQs = {}
  const whitelisted = whitelist.concat(externalUrlParameters.map((param) => param.toLowerCase()))
  for (const key in parsed) {
    if (whitelisted.includes(key.toLowerCase())) {
      emptyObj[key] = parsed[key]
    }
  }

  return emptyObj
}

export default useSearchHistory
